import { alpha } from '@mui/system/colorManipulator';
import { IconAlertCircle, IconCancel } from '@u21/tabler-icons';
import styled, { css, keyframes } from 'styled-components';
import { U21AiChatAttachmentChip } from 'app/shared/u21-ui/components/input/chat/U21AiChatAttachmentChip';
import {
  CANCELLED_MESSAGE_TEXT,
  DEFAULT_FAILED_MESSAGE,
} from 'app/shared/u21-ui/components/input/chat/constants';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Tooltip } from 'app/shared/u21-ui/components/display/U21Tooltip';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import {
  U21AiChatMessage,
  U21AiChatMessageStatus,
} from 'app/shared/u21-ui/components/input/chat/types';
import { U21Themes } from 'vendor/material-minimal/models';

type UserBubbleVariant = 'cancelled' | 'default' | 'failed' | 'pending';

const getUserBubbleVariant = (
  status: U21AiChatMessageStatus,
): UserBubbleVariant => {
  if (status === 'pending') {
    return 'pending';
  }
  if (status === 'cancelled') {
    return 'cancelled';
  }
  if (status === 'failed') {
    return 'failed';
  }
  return 'default';
};

const getDefaultFailedTooltip = (message: U21AiChatMessage): string => {
  const failedToolUse = message.toolUses?.find((t) => t.status === 'failed');
  if (failedToolUse?.errorMessage) {
    return failedToolUse.errorMessage;
  }
  return DEFAULT_FAILED_MESSAGE;
};

interface Props {
  className?: string;
  message: U21AiChatMessage;
  formattedTimestamp: string;
}

export const U21AiChatMessageBubble = ({
  className,
  formattedTimestamp,
  message,
}: Props) => {
  const isFromAgent = message.sender === 'agent';

  if (isFromAgent) {
    return (
      <MessageBubble className={className} $isFromAgent>
        <MessageText breakWord variant="body1">
          {message.text}
        </MessageText>
        <Timestamp $align="left" color="text.secondary" variant="caption">
          {formattedTimestamp}
        </Timestamp>
      </MessageBubble>
    );
  }

  const bubbleVariant = getUserBubbleVariant(message.status);
  const isFailed = message.status === 'failed';
  const isCancelled = message.status === 'cancelled';
  const failedTooltip = isFailed
    ? message.failedTooltip || getDefaultFailedTooltip(message)
    : undefined;

  return (
    <StatusAndBubbleWrapper
      className={className}
      horizontal
      spacing={0.5}
      align="center"
    >
      {isFailed && failedTooltip && (
        <U21Tooltip placement="top" tooltip={failedTooltip}>
          <StatusIconWrap>
            <FailedStatusIcon aria-label={failedTooltip} role="img" />
          </StatusIconWrap>
        </U21Tooltip>
      )}
      {isCancelled && (
        <U21Tooltip placement="top" tooltip={CANCELLED_MESSAGE_TEXT}>
          <StatusIconWrap>
            <CancelledStatusIcon
              aria-label={CANCELLED_MESSAGE_TEXT}
              role="img"
            />
          </StatusIconWrap>
        </U21Tooltip>
      )}
      <MessageBubble $isFromAgent={false} $variant={bubbleVariant} spacing={1}>
        <MessageText breakWord color="inherit" variant="body1">
          {message.text}
        </MessageText>
        {message.attachments && message.attachments.length > 0 && (
          <U21Spacer horizontal wrap>
            {message.attachments.map((attachment) => (
              <U21AiChatAttachmentChip
                fileName={attachment.name}
                key={attachment.id}
                status={{
                  embedUrl: attachment.url,
                  mimeType: attachment.mimeType,
                  state: 'success',
                }}
              />
            ))}
          </U21Spacer>
        )}
        <Timestamp $align="right" color="inherit" variant="caption">
          {formattedTimestamp}
        </Timestamp>
      </MessageBubble>
    </StatusAndBubbleWrapper>
  );
};

const pendingBackgroundShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const StatusIconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

const FailedStatusIcon = styled(IconAlertCircle)`
  color: ${({ theme }) => theme.palette.warning.main};
`;

const CancelledStatusIcon = styled(IconCancel)`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const MessageBubble = styled(U21Spacer)<{
  $isFromAgent: boolean;
  $variant?: UserBubbleVariant;
}>`
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  border-radius: ${({ theme }) => theme.shape.borderRadiusSm}px;
  border: 1px solid transparent;

  ${({ $isFromAgent, theme }) =>
    $isFromAgent &&
    css`
      background-color: ${theme.palette.background.neutral};
      color: inherit;
    `}

  ${({ $isFromAgent, $variant, theme }) =>
    !$isFromAgent &&
    $variant === 'default' &&
    css`
      background-color: ${theme.palette.primary.main};
      color: ${theme.palette.primary.contrastText};
    `}

  ${({ $isFromAgent, $variant, theme }) =>
    !$isFromAgent &&
    $variant === 'cancelled' &&
    css`
      background-color: ${theme.palette.grey[500_16]};
      border-color: ${theme.palette.grey[500_24]};
      color: ${theme.palette.text.secondary};
    `}

  ${({ $isFromAgent, $variant, theme }) =>
    !$isFromAgent &&
    $variant === 'failed' &&
    css`
      background-color: ${alpha(theme.palette.warning.main, 0.12)};
      border-color: ${alpha(theme.palette.warning.main, 0.24)};
      color: ${theme.palette.mode === U21Themes.LIGHT
        ? theme.palette.warning.dark
        : theme.palette.warning.light};
    `}

  ${({ $isFromAgent, $variant, theme }) =>
    !$isFromAgent &&
    $variant === 'pending' &&
    css`
      animation: ${pendingBackgroundShift} 3s ease infinite;
      background: linear-gradient(
        270deg,
        ${theme.palette.special.lighter},
        ${theme.palette.special.main},
        ${theme.palette.special.light}
      );
      background-size: 200% 200%;
      color: ${theme.palette.special.contrastText};
    `}
`;

const StatusAndBubbleWrapper = styled(U21Spacer)`
  max-width: 100%;
  min-width: 0;
`;

const Timestamp = styled(U21Typography)<{ $align: 'left' | 'right' }>`
  align-self: ${({ $align }) =>
    $align === 'right' ? 'flex-end' : 'flex-start'};
`;

const MessageText = styled(U21Typography)`
  white-space: pre-wrap;
`;
